import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useReducedMotion } from "motion/react";

import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getBuyerAbsoluteUrl } from "@/lib/routing/portal-urls";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { PageTransition } from "@/shared/components/PageTransition";
import { MobileScreenHeader } from "@/shared/components/MobileScreenHeader";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { LoadingSkeleton, ErrorState } from "@a2z/ui";

import { BrandingPanel } from "@/modules/settings/components/redesign/store-settings/branding-panel";
import { PaymentPanel } from "@/modules/settings/components/redesign/store-settings/payment-panel";
import { ShippingPanel } from "@/modules/settings/components/redesign/store-settings/shipping-panel";
import { InventoryPanel } from "@/modules/settings/components/redesign/store-settings/inventory-panel";
import { PoliciesPanel } from "@/modules/settings/components/redesign/store-settings/policies-panel";
import { TeamRolesPanel } from "@/modules/settings/components/redesign/store-settings/team-roles-panel";
import { CustomDomainPanel } from "@/modules/settings/components/redesign/store-settings/custom-domain-panel";
import {
  SectionNav,
  type SectionDef,
  type SectionId,
} from "@/modules/settings/components/redesign/store-settings/section-nav";
import {
  Card,
  Icons,
  tokens,
} from "@/modules/settings/components/redesign/store-settings/primitives";
import { useShippingRulesQuery } from "@/modules/settings/services/useShippingRulesQuery";
import { useBostaConnectionQuery } from "@/modules/settings/services/useBostaConnection";

type StoreSettingsPageProps = {
  storeSlug: string;
};

const POLICY_TYPE_COUNT = 3;

type SectionState = { done: number; total: number };
type SummaryItem = { id: SectionId; label: string; state: SectionState };

const VALID_SECTION_IDS: readonly SectionId[] = [
  "branding",
  "payment",
  "shipping",
  "inventory",
  "policies",
  "team",
  "domain",
];

export function StoreSettingsPage({ storeSlug }: StoreSettingsPageProps) {
  const t = useTranslations();
  const locale = useLocale();
  const ss = keys.seller.storeSettings;
  const isMobile = useIsMobile();

  const SECTIONS: SectionDef[] = [
    {
      id: "branding",
      label: t(ss.sectionBrandingLabel),
      desc: t(ss.sectionBrandingDesc),
      icon: Icons.globe,
    },
    {
      id: "payment",
      label: t(ss.sectionPaymentLabel),
      desc: t(ss.sectionPaymentDesc),
      icon: Icons.card,
    },
    {
      id: "shipping",
      label: t(ss.sectionShippingLabel),
      desc: t(ss.sectionShippingDesc),
      icon: Icons.truck,
    },
    {
      id: "inventory",
      label: t(ss.sectionInventoryLabel),
      desc: t(ss.sectionInventoryDesc),
      icon: Icons.grid,
    },
    {
      id: "policies",
      label: t(ss.sectionPoliciesLabel),
      desc: t(ss.sectionPoliciesDesc),
      icon: Icons.doc,
    },
    {
      id: "team",
      label: t(ss.sectionTeamLabel),
      desc: t(ss.sectionTeamDesc),
      icon: Icons.team,
    },
    {
      id: "domain",
      label: t(ss.sectionDomainLabel),
      desc: t(ss.sectionDomainDesc),
      icon: Icons.link,
    },
  ];

  // Persist the open section in the URL (?section=team) so a refresh keeps the
  // seller where they were and the URL is shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSection = searchParams.get("section");
  const section: SectionId =
    rawSection && (VALID_SECTION_IDS as readonly string[]).includes(rawSection)
      ? (rawSection as SectionId)
      : "branding";
  const setSection = useCallback(
    (next: SectionId) => {
      const params = new URLSearchParams(searchParams);
      if (next === "branding") params.delete("section");
      else params.set("section", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const {
    data: store,
    isLoading,
    error,
    refetch,
  } = useStoreQuery({ storeSlug });
  // Session is already cached app-wide (AppShell) — reused here for the
  // payment "active" signal so the page doesn't fetch payment options itself.
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const overview = sessionData?.overview ?? null;
  // Fetched up-front so the completion summary renders complete on first paint.
  const { data: shippingRules } = useShippingRulesQuery({ storeSlug });
  const { connection: bostaConnection } = useBostaConnectionQuery({
    storeSlug,
  });

  // Public buyer storefront URL (per-store subdomain, env-aware).
  const storefrontUrl = getBuyerAbsoluteUrl(locale, storeSlug);

  const highlights = store?.storeHighlights ?? [];
  const policies = store?.storePolicies ?? [];

  // Per-section completion from data already on hand (store + global session);
  // shipping resolves once its section is opened.
  const brandingDone = [
    Boolean(store?.logoUrl),
    Boolean(store?.description && store.description.trim().length > 0),
    highlights.length > 0,
    Boolean(store?.whatsappNumber),
  ].filter(Boolean).length;

  const depositConfigured =
    Boolean(store?.depositSettings) ||
    Boolean(store?.includeShippingInOrderDeposit);
  // A payment method is configured via an active online option OR cash on
  // delivery — COD counts so a COD-only store isn't shown as unpaid.
  const paymentDone = [
    Boolean(overview?.hasActivePaymentOptions) || Boolean(store?.acceptsCod),
    depositConfigured,
  ].filter(Boolean).length;

  const enabledPolicies = policies.filter((p) => p.isEnabled).length;

  const stateById: Record<SectionId, SectionState> = {
    branding: { done: brandingDone, total: 4 },
    payment: { done: paymentDone, total: 2 },
    shipping: {
      done:
        ((shippingRules ?? []).length > 0 ? 1 : 0) + (bostaConnection ? 1 : 0),
      total: 2,
    },
    policies: {
      done: Math.min(enabledPolicies, POLICY_TYPE_COUNT),
      total: POLICY_TYPE_COUNT,
    },
    // Inventory ships with sane defaults (threshold 10, no overselling), so it
    // isn't a required setup step — excluded from the progress summary.
    inventory: { done: 0, total: 0 },
    // Team & roles isn't a setup step — excluded from the progress summary.
    team: { done: 0, total: 0 },
    // Custom domain is optional — excluded from the setup progress summary.
    domain: { done: 0, total: 0 },
  };

  const summaryItems: SummaryItem[] = SECTIONS.filter(
    (s) => s.id !== "team" && s.id !== "inventory" && s.id !== "domain",
  ).map((s) => ({
    id: s.id,
    label: s.label,
    state: stateById[s.id],
  }));

  // Hero into the canvas chrome slot — canonical title + subtitle pattern
  // (matches Coupons / Categories). On mobile we render a light header inline.
  const storeSettingsTitle = t(keys.seller.storeSettingsTitle);
  const storeSettingsSubtitle = t(keys.seller.storeSettingsSubtitle);
  const { setHero, setHeroAux } = usePageChrome();
  useEffect(() => {
    if (isMobile) {
      setHero(null);
      return () => setHero(null);
    }
    setHero(
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
            {storeSettingsTitle}
          </h1>
          <p className="max-w-[560px] text-meta text-[color:var(--text-muted)]">
            {storeSettingsSubtitle}
          </p>
        </div>
      </div>,
    );
    return () => setHero(null);
  }, [setHero, isMobile, storeSettingsTitle, storeSettingsSubtitle]);

  // Settings doesn't use heroAux but we clear it defensively in case a
  // previously-rendered page left content in the slot.
  useEffect(() => {
    setHeroAux(null);
    return () => setHeroAux(null);
  }, [setHeroAux]);

  if (isLoading && !store) {
    return <LoadingSkeleton variant="card" rows={4} />;
  }
  if (error) {
    return <ErrorState onRetry={refetch} />;
  }

  return (
    <>
      <PageTransition className="w-full">
        {/* Mobile-only header — shared light large-title chrome (same as the
          other primary screens). */}
        {isMobile ? (
          <MobileScreenHeader
            theme="light"
            surface="var(--shell)"
            title={storeSettingsTitle}
            ariaLabel={storeSettingsTitle}
            heading={
              <h1 className="m-0 text-[23px] font-bold leading-tight tracking-[-0.025em] text-[color:var(--text-primary)]">
                {storeSettingsTitle}
              </h1>
            }
            subtitle={
              <div className="mt-[3px] text-meta text-[color:var(--text-muted)]">
                {storeSettingsSubtitle}
              </div>
            }
          />
        ) : null}

        <section
          style={{
            padding: isMobile ? "12px 16px 32px" : "24px 32px 80px",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "minmax(0, 1fr)"
              : "minmax(0, 1fr) 320px",
            gap: isMobile ? 16 : 24,
            background: tokens.bg,
            minHeight: isMobile ? undefined : "100vh",
          }}
        >
          {/* Section nav lives in the main sidebar on desktop; the mobile pill
            bar is kept here for small screens. */}
          {isMobile ? (
            <SectionNav
              sections={SECTIONS}
              section={section}
              setSection={setSection}
              tip={t(ss.navTip)}
              isMobile
            />
          ) : null}

          <main style={{ minWidth: 0 }}>
            {isMobile ? (
              <div style={{ marginBottom: 16 }}>
                <SetupSummary items={summaryItems} />
              </div>
            ) : null}
            {section === "branding" ? (
              <BrandingPanel
                storeSlug={storeSlug}
                storeName={store?.storeName ?? ""}
                description={store?.description ?? null}
                descriptionEn={store?.descriptionEn ?? null}
                logoUrl={store?.logoUrl ?? null}
                whatsappNumber={store?.whatsappNumber ?? null}
                urls={store?.urls ?? []}
                highlights={highlights}
                storefrontUrl={storefrontUrl}
                isMobile={isMobile}
              />
            ) : null}
            {section === "payment" ? (
              <PaymentPanel
                storeSlug={storeSlug}
                locale={locale}
                depositSettings={store?.depositSettings ?? null}
                includeShippingInOrderDeposit={
                  store?.includeShippingInOrderDeposit ?? false
                }
                acceptsCod={store?.acceptsCod ?? true}
                autoConfirmCodOrders={store?.autoConfirmCodOrders ?? true}
                isMobile={isMobile}
              />
            ) : null}
            {section === "shipping" ? (
              <ShippingPanel storeSlug={storeSlug} isMobile={isMobile} />
            ) : null}
            {section === "inventory" ? (
              <InventoryPanel
                storeSlug={storeSlug}
                lowStockThreshold={store?.lowStockThreshold ?? 10}
                allowOverselling={store?.allowOverselling ?? false}
                unlimitedStock={store?.unlimitedStock ?? false}
              />
            ) : null}
            {section === "policies" ? (
              <PoliciesPanel storeSlug={storeSlug} policies={policies} />
            ) : null}
            {section === "team" ? <TeamRolesPanel /> : null}
            {section === "domain" ? (
              <CustomDomainPanel storeSlug={storeSlug} />
            ) : null}
          </main>

          {!isMobile ? (
            <aside style={{ position: "sticky", top: 16, alignSelf: "start" }}>
              <SetupSummary items={summaryItems} />
            </aside>
          ) : null}
        </section>
      </PageTransition>

    </>
  );
}

type SetupSummaryProps = {
  items: SummaryItem[];
};

// Non-interactive status summary (the right rail). It reports completion; it
// does NOT navigate — SectionNav is the single navigation surface.
function SetupSummary({ items }: SetupSummaryProps): React.ReactElement {
  const t = useTranslations();
  const ss = keys.seller.storeSettings;
  const total = items.length;
  const done = items.filter((i) => i.state.done >= i.state.total).length;
  const left = total - done;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const reducedMotion = useReducedMotion();

  return (
    <Card
      title={t(ss.setupProgress)}
      desc={t(ss.setupProgressSummary, { done, total, left })}
      action={
        <span
          className="font-mono"
          style={{
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: pct === 100 ? tokens.success : tokens.text,
          }}
        >
          {pct}%
        </span>
      }
    >
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: "var(--track)",
          overflow: "hidden",
          marginTop: 2,
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 999,
            background: pct === 100 ? tokens.success : tokens.accent,
            transition: reducedMotion
              ? undefined
              : "width 0.4s cubic-bezier(.22,1,.36,1)",
          }}
        />
      </div>

      <ul
        className="m-0 list-none p-0"
        style={{
          marginTop: 10,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {items.map((item) => {
          const complete = item.state.done >= item.state.total;
          const label = `${item.state.done}/${item.state.total}`;
          return (
            <li
              key={item.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "9px 4px",
                fontSize: 13,
                fontWeight: 600,
                color: tokens.text,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 999,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: complete ? tokens.accent : "transparent",
                  boxShadow: `inset 0 0 0 2px ${complete ? tokens.accent : tokens.borderStrong}`,
                }}
              >
                {complete ? (
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--on-accent)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                ) : null}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
              <span
                className="font-mono"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: complete ? tokens.text : tokens.text3,
                  flexShrink: 0,
                }}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
